import { Link } from '@tanstack/react-router';
import { BarChart3, Image, Newspaper, Settings, Tags } from 'lucide-react';
import type { PermissionCode } from '@news-cms/shared';
import { useSession } from '@/lib/auth';

/**
 * Mobile-only persistent bottom tab bar.
 * Per design_handoff_cms spec: 18px glyph + 9-10px label, active = red.
 * Hidden on `sm:` and up — desktop relies on the sidebar.
 *
 * Each tab can declare `requires` (any-of permissions). Tabs the current
 * user can't reach are dropped — keeps mobile UI clean for author roles
 * who'd otherwise tap "ตั้งค่า" and hit a 403.
 */

interface Tab {
  to: '/' | '/posts' | '/categories' | '/media' | '/settings';
  label: string;
  icon: typeof BarChart3;
  requires?: PermissionCode[];
}

const ALL_TABS: Tab[] = [
  { to: '/', label: 'Dashboard', icon: BarChart3 },
  { to: '/posts', label: 'บทความ', icon: Newspaper },
  { to: '/categories', label: 'หมวด', icon: Tags, requires: ['category.manage'] },
  {
    to: '/media',
    label: 'Media',
    icon: Image,
    requires: ['media.upload', 'media.update', 'media.delete'],
  },
  { to: '/settings', label: 'ตั้งค่า', icon: Settings, requires: ['settings.update'] },
];

export function BottomNav() {
  const { user } = useSession();
  const userPerms = new Set(user?.permissions ?? []);
  const tabs = ALL_TABS.filter((t) => {
    if (!t.requires) return true;
    return t.requires.some((p) => userPerms.has(p));
  });

  return (
    <nav className="fixed inset-x-0 bottom-0 z-30 flex h-14 items-stretch border-t border-slate-200 bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.04)] sm:hidden dark:border-slate-800 dark:bg-slate-900">
      {tabs.map((t) => (
        <Link
          key={t.to}
          to={t.to}
          className="flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] text-slate-500 transition-colors hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-50"
          activeProps={{ className: 'text-brand-600 dark:text-brand-400' }}
          activeOptions={{ exact: t.to === '/' }}
        >
          <t.icon className="h-[18px] w-[18px]" />
          <span>{t.label}</span>
        </Link>
      ))}
    </nav>
  );
}
